// ─────────────────────────────────────────────────────────────────────────────
// Pure logic helpers for the Bill Collection Modal feature.
//
// These functions contain the feature's decision / mapping / payload-building /
// derivation / formatting logic with NO React, NO network, and NO side effects,
// so they can be unit- and property-tested (fast-check) in isolation.
//
// They mirror 1:1 the behavior documented in `combinebillcollection.md` and the
// Angular source `bill-collection-modal.component.ts`.
// ─────────────────────────────────────────────────────────────────────────────

import {
  IMAGE_EXT_REGEX,
  PDF_VIEWER_HASH,
  isCashPayment,
} from "./constants";

/**
 * Decide the default top-level tab.
 * - upload allowed  -> 'upload' (ALWAYS default to Document Upload, whether or
 *                       not documents already exist)
 * - upload locked   -> 'actions' (upload tab is disabled for this status)
 */
export function resolveDefaultTab(isUploadAllowed, _docCount) {
  return isUploadAllowed ? "upload" : "actions";
}

/**
 * Build the multipart FormData for the raw file upload endpoint (`uploadFiles`).
 * Each file is renamed to `${appID}-<5digits><ext>` and appended under `file`.
 * Also appends `onlyUrl = "true"` and `lead_id = appID`.
 *
 * @param {File[]|FileList} files
 * @param {string} appID
 * @returns {FormData}
 */
export function buildUploadFilesFormData(files, appID) {
  const formData = new FormData();
  const list = files ? Array.from(files) : [];
  list.forEach((file) => {
    const dot = file.name.lastIndexOf(".");
    const ext = dot === -1 ? "" : file.name.substring(dot);
    const randomDigits = Math.floor(Math.random() * 90000) + 10000; // 5 digits
    const newName = `${appID}-${randomDigits}${ext}`;
    formData.append("file", file, newName);
  });
  formData.append("onlyUrl", "true");
  formData.append("lead_id", appID);
  return formData;
}

/**
 * Build the `uploadFinalBillCentral` request body.
 * Always includes `app_id` and `finalBillCentral.images`.
 * For cash payments, additionally includes `finalBillObj` (from the upload entry
 * fields) and `isInsFBStatus: "Approved"`.
 *
 * @param {object} record        the BillRecord (uses payment_mode, appID)
 * @param {Array}  images        finalImgArray
 * @param {{finalBillAmount:any, netBillAmount:any}} uploadAmounts
 */
export function buildUploadMetadataPayload(record, images, uploadAmounts = {}) {
  const appId = record?.appID || "";
  const body = {
    app_id: appId,
    finalBillCentral: { images: images || [] },
  };
  if (isCashPayment(record?.payment_mode)) {
    const { finalBillAmount, netBillAmount } = uploadAmounts;
    body.finalBillObj = {
      finalBillAmount:
        finalBillAmount === "" || finalBillAmount == null
          ? null
          : Number(finalBillAmount),
      netBillAmount:
        netBillAmount === "" || netBillAmount == null
          ? null
          : Number(netBillAmount),
    };
    body.isInsFBStatus = "Approved";
  }
  return body;
}

/**
 * Build the `updateCentralDashReporting` body for the IPD-number update case.
 */
export function buildIpdUpdatePayload(appId, value, userId) {
  return {
    appId,
    updateObj: {
      ipdNoOnBill: {
        value,
        remark: "ipdNoOnBill update",
        actionTakenBy: userId,
        dateTimeIso: new Date().toISOString(),
      },
    },
  };
}

/**
 * Build the `updateCentralDashReporting` body for the generic-field update case
 * (timings & collection-summary fields).
 */
export function buildGenericFieldPayload(
  appId,
  leadId,
  fieldName,
  value,
  userId,
  userName
) {
  return {
    appId,
    updateObj: {
      [fieldName]: {
        value,
        actionTakenBy: userId,
        actionTakenByUser: userName,
        dateTimeIso: new Date().toISOString(),
      },
      leadId,
    },
  };
}

/**
 * Normalize `lensApplicableStatus` coming from getFinalBillCentral. It is stored
 * as an object ({ value, actionTakenBy, dateTimeIso }) but may also arrive as a
 * plain string. Returns the string value (empty when not set).
 */
export function readLensApplicableStatus(raw) {
  if (!raw) return "";
  if (typeof raw === "string") return raw.trim();
  if (typeof raw === "object" && raw.value != null) return String(raw.value).trim();
  return "";
}

/**
 * Decide whether the Document Upload tab is locked by the lens gate.
 * Frontend-only rule (backend decides applicability via isLensApplicable):
 *  - not applicable                         -> never locked (existing flow)
 *  - a decision already persisted (status)  -> not locked
 *  - local choice "Not Applicable"          -> not locked
 *  - local choice "Applicable"              -> locked until lens is saved
 *  - no choice yet                          -> locked
 *
 * @param {object} args
 *   { isLensApplicable, lensApplicableStatus, lensChoice, lensSaved }
 */
export function isLensLocked({
  isLensApplicable,
  lensApplicableStatus,
  lensChoice,
  lensSaved,
}) {
  if (!isLensApplicable) return false;
  if (lensApplicableStatus && String(lensApplicableStatus).trim()) return false;
  if (lensChoice === "Not Applicable") return false;
  if (lensChoice === "Applicable") return !lensSaved;
  return true;
}

/**
 * Build the `updateCentralDashReporting` body that persists the lens
 * applicability decision. Value is "Applicable" | "Not Applicable".
 */
export function buildLensApplicableStatusPayload(appId, value, userId) {
  return {
    appId,
    updateObj: {
      lensApplicableStatus: {
        value,
        actionTakenBy: userId,
        dateTimeIso: new Date().toISOString(),
      },
    },
  };
}

/**
 * Immutably record a per-document decision at `index`, leaving other indices
 * unchanged. `decision` should be a `{ _id, status, declineReason?, reason? }`.
 */
export function applyDecision(finalStatusArray, index, decision) {
  const copy = Array.isArray(finalStatusArray) ? [...finalStatusArray] : [];
  copy[index] = decision;
  return copy;
}

/**
 * Save Data is complete only when a decision exists for every document
 * (count of truthy decisions equals the document count).
 */
export function isSaveComplete(finalStatusArray, docCount) {
  const decided = (finalStatusArray || []).filter(Boolean).length;
  return decided === docCount;
}

/**
 * Approve is disabled when the record is read-only OR the doc is already Approved.
 */
export function isApproveDisabled(doc, isDisable) {
  return !!isDisable || doc?.status === "Approved";
}

/**
 * Decline is disabled when the record is read-only, the doc is already Declined,
 * or the doc originated from the Central Dashboard.
 */
export function isDeclineDisabled(doc, isDisable) {
  return (
    !!isDisable ||
    doc?.status === "Declined" ||
    doc?.source === "Central Dashboard"
  );
}

/**
 * Map a `getCollectionDetails` `collectedAtGHV` array into editable GHV rows,
 * excluding soft-deleted entries and attaching an `_original` snapshot for
 * diff-only save detection.
 */
export function mapGhvTransactions(collectedAtGHV) {
  return (collectedAtGHV || [])
    .filter((g) => !g.isDeleted)
    .map((g) => ({
      id: g.id,
      amount: g.amount,
      paymentMode: g.paymentMode,
      transactionId: g.transactionId,
      _original: {
        amount: g.amount,
        paymentMode: g.paymentMode,
        transactionId: g.transactionId,
      },
    }));
}

/**
 * Derive collection totals/gap from collectible, collected-at-hospital and the
 * list of GHV amounts.
 *   totalGhvCollected = sum(ghvAmounts)
 *   collectionGapRaw  = collectableAmount - collectedAtHospital - totalGhvCollected
 *   excessAmount      = gap < 0 ? abs(gap) : 0
 *   hasExcess         = gap < 0
 *   hasLoss           = gap > 0
 */
export function computeCollectionDerived(
  collectableAmount,
  collectedAtHospital,
  ghvAmounts
) {
  const totalGhvCollected = (ghvAmounts || []).reduce(
    (sum, a) => sum + (Number(a) || 0),
    0
  );
  const collectionGapRaw =
    (Number(collectableAmount) || 0) -
    (Number(collectedAtHospital) || 0) -
    totalGhvCollected;
  const hasExcess = collectionGapRaw < 0;
  const excessAmount = hasExcess ? Math.abs(collectionGapRaw) : 0;
  const hasLoss = collectionGapRaw > 0;
  return { totalGhvCollected, collectionGapRaw, excessAmount, hasExcess, hasLoss };
}

/**
 * Build the diff-only `saveCollectionEntry` payload.
 * - `collectionStatus` is always included.
 * - `collectionLoss` only when a loss type is set.
 * - `collectedAtHospital` only when it differs from the original.
 * - a GHV row only when it is new (no id) or differs from its `_original`.
 *
 * @param {object} state
 *   { appointmentId, status, remarks, lossType, collectionLoss,
 *     collectedAtHospital, ghvTransactions }
 * @param {object} originals { collectedAtHospital }
 */
export function buildDiffOnlySavePayload(state, originals = {}) {
  const {
    appointmentId,
    status,
    remarks,
    lossType,
    collectionLoss,
    collectedAtHospital,
    ghvTransactions,
  } = state;

  const payload = {
    appointmentId,
    collectionStatus: { status, remarks, source: "Central Dashboard" },
  };

  if (lossType) {
    payload.collectionLoss = {
      amount: collectionLoss,
      type: lossType,
      remarks,
    };
  }

  if (collectedAtHospital !== originals.collectedAtHospital) {
    payload.collectedAtHospital = {
      amount: collectedAtHospital,
      source: "Central Dashboard",
    };
  }

  const changedGhv = (ghvTransactions || []).filter((t) => {
    if (!t.id) return true; // new row
    const orig = t._original;
    return (
      !orig ||
      String(t.amount) !== String(orig.amount) ||
      t.paymentMode !== orig.paymentMode ||
      t.transactionId !== orig.transactionId
    );
  });
  if (changedGhv.length) {
    payload.collectedAtGHV = changedGhv.map((t) => ({
      ...(t.id ? { id: t.id } : {}),
      paymentMode: t.paymentMode,
      transactionId: t.transactionId,
      amount: Number(t.amount),
    }));
  }

  return payload;
}

/**
 * Format a date/time value as a local `YYYY-MM-DDTHH:mm` string suitable for a
 * `datetime-local` input. Returns "" for empty/invalid values.
 */
export function formatToDateTimeLocal(value) {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "";
  const pad = (n) => `${n}`.padStart(2, "0");
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Format a UTC timestamp as `dd-MMM-yyyy HH:mm` in IST (UTC+05:30) with a
 * trailing " IST" suffix. Returns "" for empty/invalid values.
 */
export function formatIstHistoryTimestamp(utc) {
  if (!utc) return "";
  const date = new Date(utc);
  if (isNaN(date.getTime())) return "";
  // Shift to IST by adding 5h30m to the UTC epoch, then read UTC fields.
  const IST_OFFSET_MS = (5 * 60 + 30) * 60 * 1000;
  const ist = new Date(date.getTime() + IST_OFFSET_MS);
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const pad = (n) => `${n}`.padStart(2, "0");
  const day = pad(ist.getUTCDate());
  const month = months[ist.getUTCMonth()];
  const year = ist.getUTCFullYear();
  const hours = pad(ist.getUTCHours());
  const minutes = pad(ist.getUTCMinutes());
  return `${day}-${month}-${year} ${hours}:${minutes} IST`;
}

/**
 * Classify a collection-history status string into a timeline status type by
 * keyword. Order matters: rejected > approved > pending, else info.
 */
export function classifyHistoryStatusType(status) {
  const s = (status || "").toLowerCase();
  if (s.includes("reject") || s.includes("declined")) return "rejected";
  if (s.includes("approve")) return "approved";
  if (s.includes("pending")) return "pending";
  return "info";
}

/**
 * Merge success payment-history records into existing GHV transactions,
 * mirroring the central dashboard's `fetchPaymentHistoryForCollectible`:
 * - only `status === 'success'` (case-insensitive) rows are considered
 * - match by transactionId (invoiceNumber); if an existing row lacks a paymentMode
 *   but the payment row has a vendor, replace it
 * - otherwise append as a new GHV row (dedup by _id)
 * Returns a NEW array (does not mutate the input).
 */
export function prefillGhvFromPaymentHistory(ghvTransactions, paymentRows) {
  const result = (ghvTransactions || []).map((t) => ({ ...t }));
  const rows = Array.isArray(paymentRows) ? paymentRows : [];
  const successRows = rows.filter(
    (r) => r.status && String(r.status).toLowerCase() === "success"
  );
  successRows.forEach((row) => {
    const txnId = (row.invoiceNumber || "").trim();
    const existingIdx = result.findIndex(
      (t) => (t.transactionId || "").trim() === txnId && txnId !== ""
    );
    if (existingIdx !== -1) {
      const existing = result[existingIdx];
      const existingHasMode = !!(existing.paymentMode && existing.paymentMode.trim());
      const newHasMode = !!(row.vendor && row.vendor.trim());
      if (!existingHasMode && newHasMode) {
        result[existingIdx] = {
          id: row._id,
          amount: Number(row.amount) || 0,
          paymentMode: row.vendor || "",
          transactionId: txnId,
          _original: {
            amount: Number(row.amount) || 0,
            paymentMode: row.vendor || "",
            transactionId: txnId,
          },
        };
      }
    } else {
      const alreadyById = result.some((t) => t.id && t.id === row._id);
      if (!alreadyById) {
        result.push({
          id: row._id,
          amount: Number(row.amount) || 0,
          paymentMode: row.vendor || "Cash",
          transactionId: txnId,
          _original: {
            amount: Number(row.amount) || 0,
            paymentMode: row.vendor || "Cash",
            transactionId: txnId,
          },
        });
      }
    }
  });
  return result;
}

/**
 * Map a raw `fetchFinalBill` row (data[0]) into the normalized modal `data` shape.
 * Mirrors the Angular `rebuildFromRaw` so binding matches the central dashboard 1:1.
 *
 * The row's `url` field is an array of uploaded documents; each item already carries
 * `url`, `status`, `_id`, `source`, `declineReason`, etc. — used directly as urlArray.
 */
export function mapFetchFinalBillRow(raw) {
  if (!raw) return {};
  // Reporting rows often wrap cell values as `{ value: <x>, ... }` (subdbKey:"value").
  // Unwrap so downstream prefill binds to scalars, matching the Angular data shape.
  const unwrap = (v) =>
    v && typeof v === "object" && !Array.isArray(v) && "value" in v ? v.value : v;
  let urlArray = [];
  const rawUrls = raw.url;
  if (Array.isArray(rawUrls) && rawUrls.length > 0) {
    rawUrls.forEach((x) => {
      if (x && x.Url) {
        urlArray = urlArray.concat(x.Url);
      } else {
        urlArray = urlArray.concat(x);
      }
    });
  }
  return {
    // Spread the full raw row FIRST so our normalized/unwrapped fields below win
    // (a trailing `...raw` would re-wrap scalars as `{value}` and break prefill).
    ...raw,
    urlArray,
    status: unwrap(raw.status) || "",
    name: unwrap(raw.patient_name) || "",
    patient_name: unwrap(raw.patient_name) || "",
    hospital: unwrap(raw.hospital_name) || "",
    hospital_name: unwrap(raw.hospital_name) || "",
    payment_mode: unwrap(raw.payment_mode) || "",
    city: unwrap(raw.city) || "",
    room: unwrap(raw.room_alloted) || "",
    tabKey: "Final Bill",
    appID: unwrap(raw.app_id) || "",
    ipdNoOnBill: unwrap(raw.ipdNoOnBill) || "",
    finalBillObj: raw.finalBillObj || {},
    finalBillAmount: raw.finalBillObj ? raw.finalBillObj.finalBillAmount : null,
    netBillAmount: raw.finalBillObj ? raw.finalBillObj.netBillAmount : null,
    leadId: unwrap(raw.lead_id) || "",
    lead_id: unwrap(raw.lead_id) || "",
    collectionStatus: unwrap(raw.collectionStatus) || "",
    // Final-bill status flags drive the upload-gating override
    // (isCentralFBStatus === "Approved" AND isFinanceFBStatus === "Declined").
    isCentralFBStatus: unwrap(raw.isCentralFBStatus),
    isFinanceFBStatus: unwrap(raw.isFinanceFBStatus),
    discharge_date: unwrap(raw.discharge_date) || "",
    actual_admission_date:
      unwrap(raw.actual_admission_date) || unwrap(raw.admission_date) || "",
    actualOtStartTime: unwrap(raw.actualOtStartTime) || "",
    actualOtEndTime: unwrap(raw.actualOtEndTime) || "",
    totalCollectibleAtAdmission: unwrap(raw.totalCollectibleAtAdmission) || 0,
    totalCollectibleAtDischarge: unwrap(raw.totalCollectibleAtDischarge) || 0,
    totalCollectedAtAdmission: unwrap(raw.totalCollectedAtAdmission) || 0,
    totalCollectedAtDischarge: unwrap(raw.totalCollectedAtDischarge) || 0,
  };
}

/**
 * Classify a document URL as image vs PDF and compute the viewer URL.
 * For non-images the PDF viewer hash params are appended; for images no hash.
 *
 * @param {string} rawUrl        the raw storage URL (used for extension test)
 * @param {string} [signedUrl]   the signed URL to build the viewer URL from
 *                               (defaults to rawUrl when omitted)
 * @returns {{ isImage: boolean, viewerUrl: string }}
 */
export function classifyDocUrl(rawUrl, signedUrl) {
  const base = signedUrl != null ? signedUrl : rawUrl;
  const isImage = IMAGE_EXT_REGEX.test(rawUrl || "");
  const viewerUrl = isImage ? base : `${base}${PDF_VIEWER_HASH}`;
  return { isImage, viewerUrl };
}
